import photoView from '../photoView/index.js';
import selectTilesActions from './selectTilesActions.js';

export default (route, params, options, eventId, selectedTags, availableTags, duplicatedImageIds) => ({
    ...photoView(route, params, options),
    ...selectTilesActions(),
    eventId,
    selectedTags,
    availableTags,
    duplicatedImageIds,

    init() {
        this.reset();
        this.start();

        this.initWatches();
    },

    initWatches() {
        this.$watch('selectAllItems', () => this.selectAll());
        this.$watch('deselectAllItems', () => this.deselectAll());
        this.$watch('confirm', () => this.confirmPhotos());

        this.$watch('tilesState', (tiles) => {
            Object.values(tiles).forEach((tile) => {
                if (!tile || !tile.id) return;

                if (this.isIdInSelectionRanges(tile.id)) {
                    const eventId = tile.event_id;
                    const tileId = tile.id;

                    if (!eventId || !tileId) return;

                    if (!this.selectedItems[eventId]) {
                        this.selectedItems[eventId] = this.getTagsInitialState();
                    }

                    if (
                        !this.selectedItems[eventId].manuallySelected.includes(tileId)
                        && !this.unselectedAfterShiftSelect.includes(tileId)
                        && this.shiftWasPressed
                    ) {
                        this.selectedItems[eventId].manuallySelected.push(tileId);
                    }
                }
            });
        });
    },

    isIdInSelectionRanges(id) {
        return this.selectionIdRanges?.some(range => id >= range.start_id && id <= range.end_id);
    },

    getTileElement(image, index) {
        const tileDiv = this.tileTemplate.cloneNode(true);
        tileDiv.event_id = image.event_id;

        tileDiv.style.width = `${this.tileSize.width}px`;
        tileDiv.style.height = `${this.tileSize.height}px`;

        tileDiv.classList.add('bg-gray-700');

        tileDiv.setAttribute(
            'x-bind:class',
            `{
            'border-green-500': isSelected('${index}'),
            'border-transparent': !isSelected('${index}'),
        }`,
        );

        tileDiv.setAttribute('data-tile-id', index);

        const img = tileDiv.querySelector('img');
        img.src = image.thumb;
        img.setAttribute('data-image-id', image.id);

        tileDiv.insertAdjacentHTML('beforeend', this.fillActionsAndTags(image, index));

        return tileDiv;
    },

    topImageItems(image, index) {
        const selectCheckbox = `
            <div class="absolute top-2 right-2 h-6 w-6 text-center bg-white border-none rounded-full shadow-soft hidden sm:block" x-data>
                <input
                    type="checkbox"
                    class="h-4 w-4 rounded-full p-1 border-2 border-gray-300 text-green-600 form-checkbox focus:ring-green-500"
                    :checked="isSelected('${index}')"
                />
            </div>
        `;

        const highResLink = `
            <a
                href="${image.high_res}"
                target="_blank"
                rel="noopener noreferrer"
                x-on:click="event.stopPropagation();"
                class="cursor-pointer rounded-full p-1 button hover:opacity-75 bg-gray-100"
                id="high-${index}">
                <svg class="w-5 h-5 text-blue-500" x-ref="previewIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
                </svg>
            </a>
        `;

        return `
            <div class="top-buttons buttons absolute top-0 left-0 w-full h-10 flex items-center justify-between px-2">
              ${selectCheckbox}
              ${highResLink}
            </div>
        `;
    },

    async confirmPhotos() {
        this.save();
        this.$wire.confirm();
        this.selectedItems = {};
        this.$dispatch('confirm-photos');
    },

    getParams() {
        const params = {
            event_ids: [],
            except: [],
            only: [],
            ranges: [],
        };

        Object.entries(this.selectedItems).forEach(([eventId, item]) => {
            if (!item || !item.tags) return;

            const tagEntries = Object.entries(item.tags);

            const tagsWithAllSelected = tagEntries
                .filter(([_, tagSelection]) => tagSelection.allSelected)
                .map(([tagId]) => tagId);

            if (tagsWithAllSelected.length) {
                params.event_ids.push(
                    JSON.stringify({
                        event_id: eventId,
                        tags: tagsWithAllSelected,
                    }),
                );

                if (item.manuallyUnselected?.length > 0) {
                    params.except.push(...item.manuallyUnselected);
                }

                const tagsWithManuallyUnselected = tagEntries
                    .filter(([_, tagSelection]) => tagSelection?.manuallyUnselected?.length > 0)
                    .map(([tagId, tagSelection]) => {
                        return tagSelection.manuallyUnselected.map((id) => id);
                    })
                    .flat();

                if (tagsWithManuallyUnselected.length) {
                    params.except.push(...tagsWithManuallyUnselected);
                }
            }

            if (item.manuallySelected?.length > 0) {
                params.only.push(...item.manuallySelected);
            }
        });

        if (this.selectionIdRanges?.length) {
            params.ranges = this.selectionIdRanges.map(range => ({
                start_id: range.start_id,
                end_id: range.end_id,
                except: this.shiftWasPressed && this.unselectedAfterShiftSelect?.length ?
                    this.unselectedAfterShiftSelect.filter(id =>
                        parseInt(id) >= range.start_id && parseInt(id) <= range.end_id
                    ) : []
            }));
        }

        params.except = [...new Set(params.except.flat())];
        params.only = [...new Set(params.only)].filter((id) => !params.except.includes(id));

        return {
            event_ids: JSON.stringify(params.event_ids),
            except: params.except,
            only: params.only,
            ranges: JSON.stringify(params.ranges)
        };
    },

    save() {
        try {
            fetch(routeImages, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                },
                body: JSON.stringify(this.getParams())
            });
        } catch (error) {
            console.error('Erro on saving photos:', error);
        }
    }
});
